import classNames from 'classnames'
import colors from 'color'
import {SketchPicker} from 'react-color'

import {
  TAG_TYPE__ORDER,
  TAG_TYPE__RETURN_ORDER,
  TAG_TYPE__PRODUCT,
  TAG_TYPE__MO,
  TAG_TYPE__PO,
  TAG_TYPE__STOCKTAKE,
} from '../../constants/Tags.js'
import {isPresent} from '../../utils.js'
import ColorOptions from '../../constants/ColorOptions.js'
import ConfirmModal from '../Modal/ConfirmModal.js'
import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import {
  saveOrderTag,
  addTagToOrders,
  orderTagSelector,
} from '../../../data/orderTags.js'
import {
  saveReturnOrderTag,
  addTagToReturnOrders,
  returnOrderTagSelector,
} from '../../../data/returnOrderTags.js'
import {
  saveProductTag,
  addTagToProducts,
  productTagSelector,
} from '../../../data/productTags.js'
import {saveMOTag, addTagToMOs, moTagSelector} from '../../../data/moTags.js'
import {addTagToPOs, poTagSelector, savePOTag} from '../../../data/poTags.js'
import {showMessageToast} from '../../../ordoro/Header/Toast/index.js'
import {toggleTag} from '../../../ordoro/CreateOrderPage/createOrderActions.js'
import {setRuleApplyTag} from '../../../ordoro/Settings/Rules/rulesFunctions.js'
import {currentDropdownSelector} from '../../../redux/selectors/ui/index.js'
import {
  addTagToStocktakes,
  saveStocktakeTag,
  stocktakeTagSelector,
} from '../../../data/stocktakeTags.js'

const MODAL_FORM = 'EDIT_TAG_MODAL_FORM'
const COLOR_PICKER_DROPDOWN = 'EDIT_TAG_MODAL_COLOR_PICKER'

export function showEditTagModal(tagType, tagID, meta) {
  let tag = {}

  if (tagType === TAG_TYPE__ORDER && tagID) {
    tag = orderTagSelector(getState(), {orderTagID: tagID})
  } else if (tagType === TAG_TYPE__RETURN_ORDER && tagID) {
    tag = returnOrderTagSelector(getState(), {
      returnOrderTagID: tagID,
    })
  } else if (tagType === TAG_TYPE__PRODUCT && tagID) {
    tag = productTagSelector(getState(), {
      productTagID: tagID,
    })
  } else if (tagType === TAG_TYPE__MO && tagID) {
    tag = moTagSelector(getState(), {
      moTagID: tagID,
    })
  } else if (tagType === TAG_TYPE__PO && tagID) {
    tag = poTagSelector(getState(), {
      poTagID: tagID,
    })
  } else if (tagType === TAG_TYPE__STOCKTAKE && tagID) {
    tag = stocktakeTagSelector(getState(), {
      stocktakeTagID: tagID,
    })
  }

  setForm(MODAL_FORM, {
    tagType,
    id: tagID,
    name: tag.name || '',
    color: tag.color || ColorOptions[0].color,
    meta,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export function errorsSelector(state) {
  const form = modalFormSelector(state)
  const errors = {}

  if (!isPresent(form.name)) {
    errors.name = 'Tag name is required'
    errors.preventSave = true
  }

  if (!isPresent(form.color)) {
    errors.color = 'Tag color is required'
    errors.preventSave = true
  }

  return errors
}

export async function updateTag() {
  try {
    updateModalForm({isSaving: true})

    const {tagType, id, name, color, meta} = modalFormSelector(getState())

    let tag = null
    if (tagType === TAG_TYPE__ORDER) {
      tag = await saveOrderTag(name, color, id)
    } else if (tagType === TAG_TYPE__RETURN_ORDER) {
      tag = await saveReturnOrderTag(name, color, id)
    } else if (tagType === TAG_TYPE__PRODUCT) {
      tag = await saveProductTag(name, color, id)
    } else if (tagType === TAG_TYPE__MO) {
      tag = await saveMOTag(name, color, id)
    } else if (tagType === TAG_TYPE__PO) {
      tag = await savePOTag(name, color, id)
    } else if (tagType === TAG_TYPE__STOCKTAKE) {
      tag = await saveStocktakeTag(name, color, id)
    }

    showMessageToast(`Tag (${name}) was ${id ? 'updated' : 'created'}`)

    closeModal()

    await handleMeta(tag, meta, tagType)
  } catch (err) {
    updateModalForm({
      serverError: `Error saving tag: ${err.message || err.error_message}`,
      isSaving: false,
    })
  }
}

async function handleMeta(tag, meta, tagType) {
  if (!meta) {
    return
  }

  if (tagType === TAG_TYPE__ORDER && meta.addTagToOrderNumbers) {
    await addTagToOrders(tag.id, meta.addTagToOrderNumbers)
  }

  if (tagType === TAG_TYPE__ORDER && meta.addTagToRuleID) {
    setRuleApplyTag(meta.addTagToRuleID, meta.index, tag.id)
  }

  if (tagType === TAG_TYPE__ORDER && meta.addTagToNewOrder) {
    toggleTag(tag.id, true)
  }

  if (tagType === TAG_TYPE__RETURN_ORDER && meta.addTagToReferenceIDs) {
    await addTagToReturnOrders(tag.id, meta.addTagToReferenceIDs)
  }

  if (tagType === TAG_TYPE__PRODUCT && meta.addTagToSKUs) {
    await addTagToProducts(tag.id, meta.addTagToSKUs)
  }

  if (tagType === TAG_TYPE__PRODUCT && meta.addProductTagToRuleOnChange) {
    meta.addProductTagToRuleOnChange(tag.id)
  }

  if (tagType === TAG_TYPE__MO && meta.addTagToMOReferenceIDs) {
    await addTagToMOs(tag.id, meta.addTagToMOReferenceIDs)
  }

  if (tagType === TAG_TYPE__PO && meta.addTagToPOIDs) {
    await addTagToPOs(tag.id, meta.addTagToPOIDs)
  }

  if (tagType === TAG_TYPE__STOCKTAKE && meta.addTagToStocktakeReferenceIDs) {
    await addTagToStocktakes(tag.id, meta.addTagToStocktakeReferenceIDs)
  }
}

function EditTagModal({form}) {
  const isColorFocused =
    useSelector(currentDropdownSelector) === COLOR_PICKER_DROPDOWN
  const errors = useSelector(errorsSelector)
  const title = form.id ? 'Edit Tag' : 'Create a New Tag'

  return (
    <ConfirmModal
      title={title}
      modalSize="sm-md"
      confirmText="Save"
      cancelText="Cancel"
      onConfirm={() => updateTag()}
      onCancel={() => closeModal()}
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
    >
      <div className="row">
        <div className="columns medium-6">
          <ul className="list--form">
            <li className={classNames('list__item', {error: errors.name})}>
              <label htmlFor="id_tag_name">Tag Name</label>
              <input
                className="input--tall"
                type="text"
                name="tag_name"
                value={form.name}
                onChange={(event) =>
                  updateModalForm({name: event.target.value})
                }
              />
              {errors.name && (
                <small className="error error-message">{errors.name}</small>
              )}
            </li>
            <li className={classNames('list__item', {error: errors.color})}>
              <label htmlFor="id_tag_color">Tag Color</label>
              <div
                className={classNames('wrap--tag-color flex', {
                  focused: isColorFocused,
                })}
                data-dropdown={COLOR_PICKER_DROPDOWN}
                data-dropdown-prevent-close
              >
                <div
                  className="prefix--tag-color"
                  style={{
                    backgroundColor: form.color,
                  }}
                />
                <input
                  className="input--tall input--tag-color"
                  type="text"
                  name="tag_color"
                  value={form.color}
                  onChange={(event) =>
                    updateModalForm({color: event.target.value})
                  }
                />
              </div>
              {errors.color && (
                <small className="error error-message">{errors.color}</small>
              )}
            </li>
          </ul>
        </div>
        {isColorFocused && (
          <div
            className="wrap--color-picker"
            data-dropdown={COLOR_PICKER_DROPDOWN}
            data-dropdown-prevent-close
          >
            <SketchPicker
              onChangeComplete={(color) => {
                if (color.rgb.a !== 1) {
                  updateModalForm({
                    color: colors({
                      r: color.rgb.r,
                      g: color.rgb.g,
                      b: color.rgb.b,
                    })
                      .alpha(color.rgb.a)
                      .string(),
                  })
                } else {
                  updateModalForm({color: color.hex})
                }
              }}
              color={form.color}
              presetColors={ColorOptions.map(
                (colorOption) => colorOption.color,
              )}
            />
          </div>
        )}
      </div>
    </ConfirmModal>
  )
}

export default onlyIfForm(EditTagModal, modalFormSelector)
